import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { boardService } from '../../../../lib/api/boards';

interface ExportOperationsModalProps {
    isOpen: boolean;
    onClose: () => void;
    journalId: string;
    operationIds: Set<string>;
}

type ExportFormat = 'csv-excel' | 'csv-plain';

export const ExportOperationsModal: React.FC<ExportOperationsModalProps> = ({
    isOpen,
    onClose,
    journalId,
    operationIds
}) => {
    const { t } = useTranslation('common');
    const [format, setFormat] = useState<ExportFormat>('csv-excel');
    const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setFormat('csv-excel');
            setIsExporting(false);
        }
    }, [isOpen]);

    const handleExport = async (e: React.FormEvent) => {
        e.preventDefault();
        if (operationIds.size === 0 || isExporting) return;

        setIsExporting(true);
        try {
            const blob = await boardService.exportJournalOperations(journalId, Array.from(operationIds), format);

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `exported_operations_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            onClose();
        } catch (error) {
            console.error('Export failed', error);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60] transition-opacity"
                        onClick={onClose}
                    />
                    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 pointer-events-none">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            className="bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl w-full max-w-sm pointer-events-auto overflow-hidden text-left"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="p-6 border-b border-neutral-100 dark:border-neutral-800">
                                <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-100">
                                    {t('exportOperations.title', 'Export Operations')}
                                </h3>
                                <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                                    {t('exportOperations.subtitle', 'Choose the best format for your needs')}
                                </p>
                            </div>

                            <form onSubmit={handleExport} className="p-6 space-y-4">
                                <div className="space-y-3">
                                    <label className={`flex items-center p-4 border rounded-xl cursor-pointer transition-all ${format === 'csv-excel' ? 'border-accent-primary bg-accent-primary/5 dark:bg-accent-primary/10 ring-1 ring-accent-primary' : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600 bg-white dark:bg-neutral-800'}`}>
                                        <input
                                            type="radio"
                                            name="format"
                                            value="csv-excel"
                                            checked={format === 'csv-excel'}
                                            onChange={() => setFormat('csv-excel')}
                                            className="w-4 h-4 text-accent-primary border-neutral-300 dark:border-neutral-600 focus:ring-accent-primary bg-white dark:bg-neutral-700"
                                        />
                                        <div className="ml-3">
                                            <span className="block text-sm font-medium text-neutral-900 dark:text-neutral-100">
                                                {t('exportOperations.formats.csvExcel', 'CSV for Excel')}
                                            </span>
                                            <span className="block text-xs text-neutral-500 dark:text-neutral-400">
                                                {t('exportOperations.formats.csvExcelDesc', 'Optimized for Excel, Numbers, and other spreadsheet tools')}
                                            </span>
                                        </div>
                                    </label>

                                    <label className={`flex items-center p-4 border rounded-xl cursor-pointer transition-all ${format === 'csv-plain' ? 'border-accent-primary bg-accent-primary/5 dark:bg-accent-primary/10 ring-1 ring-accent-primary' : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600 bg-white dark:bg-neutral-800'}`}>
                                        <input
                                            type="radio"
                                            name="format"
                                            value="csv-plain"
                                            checked={format === 'csv-plain'}
                                            onChange={() => setFormat('csv-plain')}
                                            className="w-4 h-4 text-accent-primary border-neutral-300 dark:border-neutral-600 focus:ring-accent-primary bg-white dark:bg-neutral-700"
                                        />
                                        <div className="ml-3">
                                            <span className="block text-sm font-medium text-neutral-900 dark:text-neutral-100">
                                                {t('exportOperations.formats.csvPlain', 'Plain CSV')}
                                            </span>
                                            <span className="block text-xs text-neutral-500 dark:text-neutral-400">
                                                {t('exportOperations.formats.csvPlainDesc', 'Standard comma-separated values file')}
                                            </span>
                                        </div>
                                    </label>
                                </div>

                                <div className="flex justify-end gap-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="px-4 py-2.5 text-sm font-medium text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-50 dark:hover:bg-neutral-800 rounded-xl transition-colors"
                                    >
                                        {t('common.cancel', 'Cancel')}
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isExporting}
                                        className="px-6 py-2.5 bg-accent-primary text-white text-sm font-medium rounded-xl hover:bg-accent-primary/90 focus:ring-4 focus:ring-accent-primary/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-accent-primary/20 flex items-center gap-2"
                                    >
                                        {isExporting ? (
                                            <>
                                                <span className="animate-spin h-4 w-4 text-white" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_8886e70e.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_8886e70e.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                                {t('common.exporting', 'Exporting...')}
                                            </>
                                        ) : (
                                            <>
                                                <span className="w-4 h-4" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_97a87d0d.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_97a87d0d.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                                {t('actions.export', 'Export')}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
};
